<template>
  <div class="mz-desktop">

    <!-- ══ SIDEBAR NAV ══ -->
    <aside class="mz-sidebar">
      <div class="sidebar-top">
        <div class="sidebar-brand">
          <svg width="32" height="32" viewBox="0 0 48 48" fill="none">
            <rect width="48" height="48" rx="14" fill="#d48c1c"/>
            <path d="M12 34V18l8 10 4-6 4 6 8-10v16" stroke="#0c0a0e" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
          </svg>
          <span class="sidebar-wordmark">Muzilly</span>
        </div>
        <nav class="sidebar-nav">
          <button v-for="tab in navTabs" :key="tab.id"
            class="nav-item" :class="{ active: tab.id === 'profile' }"
            @click="navigate(tab.id)">
            <span class="nav-item-icon" v-html="tab.svg"></span>
            <span class="nav-item-label">{{ tab.label }}</span>
            <span v-if="tab.badge" class="nav-badge">{{ tab.badge }}</span>
          </button>
        </nav>
      </div>
      <div class="sidebar-user">
        <div class="su-av" :style="{ background: roleColors[currentRole] }">{{ userInitials }}</div>
        <div class="su-info">
          <div class="su-name">{{ profile.name }}</div>
          <div class="su-role">{{ roleLabelMap[currentRole] }}</div>
        </div>
        <button class="su-logout" @click="navigate('home')">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
      </div>
    </aside>

    <!-- ══ MAIN CONTENT ══ -->
    <div class="mz-content">

      <header class="mz-topbar">
        <div class="topbar-left">
          <h2 class="topbar-title">Profile</h2>
        </div>
        <div class="topbar-right">
          <div class="role-switcher">
            <button v-for="r in ['fan','artist','rising']" :key="r"
              class="role-pill" :class="{ active: currentRole === r }"
              @click="currentRole = r">{{ rolePillLabel[r] }}</button>
          </div>
          <button class="topbar-icon">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
            <span class="t-badge">5</span>
          </button>
        </div>
      </header>

      <!-- ══════════ PROFILE HERO ══════════ -->
      <div class="profile-page">

        <!-- Hero header — shared across all roles, content differs -->
        <div class="profile-hero" :style="{ '--role-color': roleColors[currentRole] }">
          <div class="hero-backdrop"></div>
          <div class="hero-content">
            <div class="hero-left">
              <div class="profile-avatar" :style="{ background: roleColors[currentRole] }">
                {{ userInitials }}
                <div v-if="currentRole === 'artist'" class="av-verified">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke="#0c0a0e" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </div>
              </div>
              <div class="hero-info">
                <div class="hero-name">{{ profile.name }}</div>
                <div class="hero-handle">@{{ profile.handle }}</div>
                <div class="hero-tags">
                  <span v-for="tag in profile.tags" :key="tag" class="hero-tag">{{ tag }}</span>
                </div>
              </div>
            </div>
            <div class="hero-right">
              <!-- Fan: member since + tier -->
              <div v-if="currentRole === 'fan'" class="hero-stats">
                <div class="hstat"><div class="hstat-num">{{ profile.following }}</div><div class="hstat-label">Following</div></div>
                <div class="hstat-div"></div>
                <div class="hstat"><div class="hstat-num">{{ profile.subscriptions }}</div><div class="hstat-label">Subscriptions</div></div>
                <div class="hstat-div"></div>
                <div class="hstat"><div class="hstat-num">{{ profile.memberSince }}</div><div class="hstat-label">Member since</div></div>
              </div>
              <!-- Artist: subscriber count + earnings -->
              <div v-if="currentRole === 'artist'" class="hero-stats">
                <div class="hstat"><div class="hstat-num">{{ profile.subscribers }}</div><div class="hstat-label">Subscribers</div></div>
                <div class="hstat-div"></div>
                <div class="hstat"><div class="hstat-num">{{ profile.totalPlays }}</div><div class="hstat-label">Total plays</div></div>
                <div class="hstat-div"></div>
                <div class="hstat"><div class="hstat-num">{{ profile.totalEarned }}</div><div class="hstat-label">Total earned</div></div>
              </div>
              <!-- Rising: discovery score -->
              <div v-if="currentRole === 'rising'" class="hero-stats">
                <div class="hstat"><div class="hstat-num teal">{{ profile.discoveryScore }}</div><div class="hstat-label">Discovery score</div></div>
                <div class="hstat-div"></div>
                <div class="hstat"><div class="hstat-num">{{ profile.demoPlays }}</div><div class="hstat-label">Demo plays</div></div>
                <div class="hstat-div"></div>
                <div class="hstat"><div class="hstat-num">{{ profile.cosigns }}</div><div class="hstat-label">Co-signs</div></div>
              </div>
              <div class="hero-actions">
                <button class="btn-edit" @click="editMode = !editMode">{{ editMode ? 'Save profile' : 'Edit profile' }}</button>
                <button v-if="currentRole === 'artist'" class="btn-share">Share page →</button>
              </div>
            </div>
          </div>
          <div class="hero-bio">{{ profile.bio }}</div>
        </div>

        <!-- ══ FAN PROFILE BODY ══ -->
        <div v-if="currentRole === 'fan'" class="profile-body">
          <div class="col-main">

            <!-- Edit mode -->
            <div v-if="editMode" class="edit-card">
              <div class="edit-title">Edit profile</div>
              <div class="edit-fields">
                <div class="field-group"><label>Display name</label><input type="text" v-model="profile.name" class="field-input"/></div>
                <div class="field-group"><label>Handle</label><input type="text" v-model="profile.handle" class="field-input" /></div>
                <div class="field-group"><label>Bio</label><textarea v-model="profile.bio" class="field-input field-ta"></textarea></div>
                <div class="field-group"><label>Favourite genres</label>
                  <div class="genre-check-grid">
                    <label v-for="g in allGenres" :key="g" class="genre-check">
                      <input type="checkbox" :value="g" v-model="profile.tags"/> {{ g }}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Subscriptions -->
            <div class="section-head"><span class="section-label">Active subscriptions</span><span class="section-count">{{ fanSubscriptions.length }} artists</span></div>
            <div class="sub-grid">
              <div v-for="s in fanSubscriptions" :key="s.id" class="sub-card">
                <div class="sub-av" :style="{ background: s.color }">{{ s.initials }}<div v-if="s.live" class="av-live">LIVE</div></div>
                <div class="sub-info">
                  <div class="sub-name">{{ s.name }}</div>
                  <div class="sub-genre">{{ s.genre }}</div>
                  <div class="sub-since">Member since {{ s.since }}</div>
                </div>
                <div class="sub-right">
                  <div class="sub-tier" :class="'tier-'+s.tier">{{ s.tier }}</div>
                  <button class="sub-manage">Manage</button>
                </div>
              </div>
            </div>

            <!-- Listening history -->
            <div class="section-head" style="margin-top:32px"><span class="section-label">Recently played</span></div>
            <div class="history-list">
              <div v-for="h in listeningHistory" :key="h.id" class="history-row">
                <div class="h-thumb" :style="{ background: h.color }"><svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M9 18V5l12-2v13" stroke="rgba(255,255,255,0.7)" stroke-width="1.5" stroke-linecap="round"/><circle cx="6" cy="18" r="3" stroke="rgba(255,255,255,0.7)" stroke-width="1.5"/><circle cx="18" cy="16" r="3" stroke="rgba(255,255,255,0.7)" stroke-width="1.5"/></svg></div>
                <div class="h-info"><div class="h-track">{{ h.track }}</div><div class="h-artist">{{ h.artist }}</div></div>
                <div class="h-time">{{ h.when }}</div>
                <button class="h-play">▶</button>
              </div>
            </div>
          </div>

          <div class="col-side">
            <div class="section-head"><span class="section-label">Account</span></div>
            <div class="account-card">
              <div class="ac-row"><span class="ac-label">Email</span><span class="ac-val">jordan@email.com</span></div>
              <div class="ac-row"><span class="ac-label">Plan</span><span class="ac-val amber">Free listener</span></div>
              <div class="ac-row"><span class="ac-label">Member since</span><span class="ac-val">Jan 2025</span></div>
            </div>
            <button class="upgrade-btn">Upgrade to member →</button>

            <div class="section-head" style="margin-top:28px"><span class="section-label">Favourite genres</span></div>
            <div class="tag-cloud">
              <span v-for="tag in profile.tags" :key="tag" class="tag-pill">{{ tag }}</span>
            </div>

            <div class="section-head" style="margin-top:28px"><span class="section-label">Membership perks</span></div>
            <div class="perk-list">
              <div class="perk-row locked"><svg width="13" height="13" viewBox="0 0 24 24" fill="none"><rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" stroke-width="1.5"/><path d="M7 11V7a5 5 0 0110 0v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>Join live chat in rooms</div>
              <div class="perk-row locked"><svg width="13" height="13" viewBox="0 0 24 24" fill="none"><rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" stroke-width="1.5"/><path d="M7 11V7a5 5 0 0110 0v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>Read & post comments</div>
              <div class="perk-row locked"><svg width="13" height="13" viewBox="0 0 24 24" fill="none"><rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" stroke-width="1.5"/><path d="M7 11V7a5 5 0 0110 0v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>Access members-only drops</div>
              <div class="perk-row locked"><svg width="13" height="13" viewBox="0 0 24 24" fill="none"><rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" stroke-width="1.5"/><path d="M7 11V7a5 5 0 0110 0v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>DM artists directly</div>
            </div>
          </div>
        </div>

        <!-- ══ ARTIST PROFILE BODY ══ -->
        <div v-if="currentRole === 'artist'" class="profile-body">
          <div class="col-main">

            <!-- Edit mode -->
            <div v-if="editMode" class="edit-card">
              <div class="edit-title">Edit artist profile</div>
              <div class="edit-fields">
                <div class="field-group"><label>Artist name</label><input type="text" v-model="profile.name" class="field-input"/></div>
                <div class="field-group"><label>Handle</label><input type="text" v-model="profile.handle" class="field-input"/></div>
                <div class="field-group"><label>Bio</label><textarea v-model="profile.bio" class="field-input field-ta"></textarea></div>
                <div class="field-group"><label>Location</label><input type="text" v-model="profile.location" class="field-input" placeholder="e.g. Lagos, Nigeria"/></div>
                <div class="field-group"><label>Subscription price ($/month)</label><input type="number" v-model="profile.subPrice" class="field-input"/></div>
                <div class="field-group"><label>Genres</label>
                  <div class="genre-check-grid">
                    <label v-for="g in allGenres" :key="g" class="genre-check"><input type="checkbox" :value="g" v-model="profile.tags"/> {{ g }}</label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Earnings overview -->
            <div class="section-head"><span class="section-label">Earnings overview</span><button class="section-more">Export →</button></div>
            <div class="earnings-strip">
              <div class="earn-tile"><div class="et-num">$1,240</div><div class="et-label">This month</div></div>
              <div class="earn-tile"><div class="et-num">$14.6k</div><div class="et-label">All time</div></div>
              <div class="earn-tile"><div class="et-num">847</div><div class="et-label">Active subs</div></div>
              <div class="earn-tile"><div class="et-num">$8.70</div><div class="et-label">Revenue / sub</div></div>
            </div>

            <!-- Releases -->
            <div class="section-head" style="margin-top:28px"><span class="section-label">Your releases</span><button class="section-more">+ New drop</button></div>
            <div class="release-table">
              <div class="rt-head"><span>Track</span><span>Plays</span><span>Comments</span><span>Status</span><span>Earned</span></div>
              <div v-for="rel in artistReleases" :key="rel.id" class="rt-row">
                <div class="rt-track">
                  <div class="rt-art" :style="{ background: rel.color }"><svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M9 18V5l12-2v13" stroke="rgba(255,255,255,0.7)" stroke-width="1.5" stroke-linecap="round"/><circle cx="6" cy="18" r="3" stroke="rgba(255,255,255,0.7)" stroke-width="1.5"/><circle cx="18" cy="16" r="3" stroke="rgba(255,255,255,0.7)" stroke-width="1.5"/></svg></div>
                  <div><div class="rt-title">{{ rel.title }}</div><div class="rt-date">{{ rel.date }}</div></div>
                </div>
                <span class="rt-stat">{{ rel.plays }}</span>
                <span class="rt-stat">{{ rel.comments }}</span>
                <span class="rt-status" :class="'status-'+rel.status">{{ rel.status }}</span>
                <span class="rt-earn">+${{ rel.earned }}</span>
              </div>
            </div>

            <!-- Upcoming rooms -->
            <div class="section-head" style="margin-top:28px"><span class="section-label">Your upcoming rooms</span><button class="section-more">Schedule →</button></div>
            <div class="upcoming-rooms">
              <div v-for="r in artistUpcomingRooms" :key="r.id" class="upcoming-room-row">
                <div class="urr-time"><div class="urr-val">{{ r.time }}</div><div class="urr-day">{{ r.day }}</div></div>
                <div class="urr-div"></div>
                <div class="urr-info"><div class="urr-title">{{ r.title }}</div><div class="urr-type-pill" :class="'pill-'+r.typeKey">{{ r.type }}</div></div>
                <div class="urr-rsvp">{{ r.rsvp }} RSVPs</div>
                <button class="urr-edit">Edit</button>
              </div>
            </div>
          </div>

          <div class="col-side">
            <div class="section-head"><span class="section-label">Public profile</span></div>
            <div class="public-profile-preview">
              <div class="pp-av" :style="{ background: roleColors[currentRole] }">{{ userInitials }}</div>
              <div class="pp-name">{{ profile.name }}</div>
              <div class="pp-handle">@{{ profile.handle }}</div>
              <div class="pp-bio">{{ profile.bio }}</div>
              <div class="pp-tags"><span v-for="tag in profile.tags" :key="tag" class="tag-pill">{{ tag }}</span></div>
              <button class="pp-subscribe">Subscribe · ${{ profile.subPrice }}/mo</button>
              <button class="pp-view">View public page →</button>
            </div>

            <div class="section-head" style="margin-top:28px"><span class="section-label">Top subscribers</span></div>
            <div class="sub-list-mini">
              <div v-for="s in topSubscribers" :key="s.id" class="slm-row">
                <div class="slm-av" :style="{ background: s.color }">{{ s.initials }}</div>
                <div class="slm-info"><div class="slm-name">{{ s.name }}</div><div class="slm-since">Since {{ s.since }}</div></div>
                <div class="slm-streak">{{ s.streak }}</div>
              </div>
            </div>

            <div class="section-head" style="margin-top:28px"><span class="section-label">Payout settings</span></div>
            <div class="account-card">
              <div class="ac-row"><span class="ac-label">Connected</span><span class="ac-val teal">Stripe active</span></div>
              <div class="ac-row"><span class="ac-label">Payout day</span><span class="ac-val">1st of month</span></div>
              <div class="ac-row"><span class="ac-label">Next payout</span><span class="ac-val">May 1 · $1,240</span></div>
            </div>
          </div>
        </div>

        <!-- ══ RISING PROFILE BODY ══ -->
        <div v-if="currentRole === 'rising'" class="profile-body">
          <div class="col-main">

            <!-- Edit mode -->
            <div v-if="editMode" class="edit-card">
              <div class="edit-title">Edit your artist profile</div>
              <div class="edit-fields">
                <div class="field-group"><label>Name</label><input type="text" v-model="profile.name" class="field-input"/></div>
                <div class="field-group"><label>Handle</label><input type="text" v-model="profile.handle" class="field-input"/></div>
                <div class="field-group"><label>Bio</label><textarea v-model="profile.bio" class="field-input field-ta"></textarea></div>
                <div class="field-group"><label>Location</label><input type="text" v-model="profile.location" class="field-input" placeholder="e.g. London, UK"/></div>
                <div class="field-group"><label>Looking to collaborate on</label><input type="text" v-model="profile.collabNote" class="field-input" placeholder="e.g. R&B vocals, songwriting"/></div>
                <div class="field-group"><label>Genres</label>
                  <div class="genre-check-grid">
                    <label v-for="g in allGenres" :key="g" class="genre-check"><input type="checkbox" :value="g" v-model="profile.tags"/> {{ g }}</label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Discovery score card -->
            <div class="discovery-card-full">
              <div class="dcf-left">
                <div class="dcf-eyebrow">Discovery score</div>
                <div class="dcf-num teal">{{ profile.discoveryScore }}<span class="dcf-max">/100</span></div>
                <div class="dcf-bar-wrap"><div class="dcf-bar"><div class="dcf-fill" :style="{ width: profile.discoveryScore+'%' }"></div></div></div>
                <div class="dcf-sub">Complete your profile to get seen by established artists</div>
              </div>
              <div class="dcf-right">
                <div class="dcf-label">Boost your score</div>
                <div class="dcf-tasks">
                  <div v-for="t in discoveryTasks" :key="t.label" class="dcf-task" :class="{ done: t.done }">
                    <div class="task-check" :class="{ done: t.done }">
                      <svg v-if="t.done" width="9" height="9" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                    </div>
                    <span class="task-label">{{ t.label }}</span>
                    <span class="task-pts" :class="{ done: t.done }">{{ t.done ? 'Done' : '+'+t.pts }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Demo reel -->
            <div class="section-head" style="margin-top:28px"><span class="section-label">Your demos</span><button class="section-more">+ Drop a demo</button></div>
            <div class="demo-table">
              <div class="dt-head"><span>Demo</span><span>Plays</span><span>Co-signs</span><span>Dropped</span></div>
              <div v-for="d in risingDemos" :key="d.id" class="dt-row">
                <div class="dt-track">
                  <div class="dt-art" :style="{ background: d.color }"><svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M9 18V5l12-2v13" stroke="rgba(255,255,255,0.7)" stroke-width="1.5" stroke-linecap="round"/><circle cx="6" cy="18" r="3" stroke="rgba(255,255,255,0.7)" stroke-width="1.5"/><circle cx="18" cy="16" r="3" stroke="rgba(255,255,255,0.7)" stroke-width="1.5"/></svg></div>
                  <div><div class="dt-title">{{ d.title }}</div><div class="dt-genre">{{ d.genre }}</div></div>
                </div>
                <span class="dt-stat">{{ d.plays }}</span>
                <span class="dt-cosigns">{{ d.cosigns }} <span v-if="d.cosigns > 0" class="teal">✓</span></span>
                <span class="dt-date">{{ d.date }}</span>
              </div>
            </div>

            <!-- Co-signs received -->
            <div class="section-head" style="margin-top:28px"><span class="section-label">Co-signs received</span></div>
            <div v-if="risingCosigns.length" class="cosign-received-list">
              <div v-for="cs in risingCosigns" :key="cs.id" class="csr-row">
                <div class="csr-av" :style="{ background: cs.color }">{{ cs.initials }}</div>
                <div class="csr-body">
                  <div class="csr-from">{{ cs.name }} <span class="csr-role">· Verified artist</span></div>
                  <div class="csr-quote">"{{ cs.quote }}"</div>
                </div>
                <div class="csr-when">{{ cs.when }}</div>
              </div>
            </div>
            <div v-else class="empty-state">No co-signs yet — drop more demos to get noticed.</div>
          </div>

          <div class="col-side">
            <div class="section-head"><span class="section-label">Your artist card</span></div>
            <div class="public-profile-preview">
              <div class="pp-av" :style="{ background: roleColors[currentRole] }">{{ userInitials }}</div>
              <div class="pp-name">{{ profile.name }}</div>
              <div class="pp-handle">@{{ profile.handle }}</div>
              <div class="pp-bio">{{ profile.bio }}</div>
              <div class="pp-tags"><span v-for="tag in profile.tags" :key="tag" class="tag-pill">{{ tag }}</span></div>
              <div v-if="profile.collabNote" class="pp-collab-note">Open to: {{ profile.collabNote }}</div>
              <button class="pp-view">View public card →</button>
            </div>

            <div class="section-head" style="margin-top:28px"><span class="section-label">Artists watching you</span></div>
            <div class="sub-list-mini">
              <div v-for="a in artistsWatching" :key="a.id" class="slm-row">
                <div class="slm-av" :style="{ background: a.color }">{{ a.initials }}</div>
                <div class="slm-info"><div class="slm-name">{{ a.name }}</div><div class="slm-since">{{ a.genre }}</div></div>
                <button class="slm-reach">Message</button>
              </div>
            </div>

            <div class="section-head" style="margin-top:28px"><span class="section-label">Collab interests</span></div>
            <div class="tag-cloud" style="margin-bottom:12px">
              <span v-for="tag in profile.tags" :key="tag" class="tag-pill">{{ tag }}</span>
            </div>
            <div v-if="profile.collabNote" class="collab-note-box">{{ profile.collabNote }}</div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ProfileView",
  data() {
    return {
      currentRole: "fan",
      editMode: false,
      rolePillLabel: { fan:"Fan view", artist:"Artist view", rising:"Rising view" },
      roleLabelMap:  { fan:"Fan member", artist:"Verified artist", rising:"Rising artist" },
      roleColors:    { fan:"#2e5b7e", artist:"#8b3a3a", rising:"#6b3a5a" },
      allGenres: ["R&B","Afrobeats","Hip-Hop","Amapiano","UK Drill","Soul","Trap","Dancehall"],
      profiles: {
        fan: {
          name:"Jordan", handle:"jordanlistens", bio:"Music is the only thing that makes sense. Fan of real sound, real stories.",
          tags:["R&B","Afrobeats","Soul"], following:"12", subscriptions:"3", memberSince:"Jan 2025",
          subPrice:null, location:"", collabNote:"",
        },
        artist: {
          name:"Ayo Blaze", handle:"ayoblaze", bio:"Afrobeats from Lagos to the world. Unreleased music lives here first. Subscribe to be in the room.",
          tags:["Afrobeats","R&B"], subscribers:"1.2k", totalPlays:"6.4k", totalEarned:"$14.6k",
          subPrice:"9.99", location:"Lagos, Nigeria", collabNote:"",
        },
        rising: {
          name:"Mara Sol", handle:"marasol", bio:"R&B and soul from South London. Working on something real. Open to collabs with producers and vocalists.",
          tags:["R&B","Soul"], discoveryScore:62, demoPlays:"580", cosigns:"1",
          subPrice:null, location:"London, UK", collabNote:"Looking for: producers, songwriters, vocalists",
        },
      },
      fanSubscriptions: [
        { id:1, name:"Ayo Blaze",  initials:"AB", color:"#8b3a3a", genre:"Afrobeats", since:"Jan 2025", tier:"member", live:true  },
        { id:2, name:"Luna Wave",  initials:"LW", color:"#2e5b7e", genre:"R&B",       since:"Feb 2025", tier:"member", live:false },
        { id:3, name:"Kxng Draco", initials:"KD", color:"#4a3a6b", genre:"Hip-Hop",   since:"Mar 2025", tier:"member", live:false },
      ],
      listeningHistory: [
        { id:1, track:"Sundown (Prod. Mixx)",   artist:"Ayo Blaze",  color:"#8b3a3a", when:"2h ago"     },
        { id:2, track:"Late Night Ocean",       artist:"Luna Wave",  color:"#2e5b7e", when:"5h ago"     },
        { id:3, track:"Crown Chronicles Vol.2", artist:"Kxng Draco", color:"#4a3a6b", when:"Yesterday"  },
        { id:4, track:"Lagos 3AM",              artist:"Sade Femi",  color:"#3a6b4a", when:"2 days ago" },
        { id:5, track:"Late Fade",              artist:"Ayo Blaze",  color:"#8b3a3a", when:"3 days ago" },
      ],
      artistReleases: [
        { id:1, title:"Sundown (Prod. Mixx)",   color:"#8b3a3a", plays:"1.4k", comments:24, status:"members", earned:"186", date:"Mar 2025" },
        { id:2, title:"Late Fade",              color:"#2e5b7e", plays:"890",  comments:11, status:"public",  earned:"94",  date:"Feb 2025" },
        { id:3, title:"Crown Chronicles Vol.2", color:"#4a3a6b", plays:"2.1k", comments:41, status:"members", earned:"312", date:"Jan 2025" },
        { id:4, title:"Lagos 3AM",              color:"#3a6b4a", plays:"654",  comments:9,  status:"public",  earned:"72",  date:"Dec 2024" },
      ],
      artistUpcomingRooms: [
        { id:1, time:"9:00 PM", day:"Tonight",  title:"Late night vibes · unreleased cuts", typeKey:"session", type:"Session", rsvp:"47"  },
        { id:2, time:"3:00 PM", day:"Tomorrow", title:"Subscriber Q&A · production deep dive", typeKey:"qa", type:"Q&A",     rsvp:"112" },
      ],
      topSubscribers: [
        { id:1, name:"Keiko Balogun", initials:"KB", color:"#8b3a3a", since:"Jan 2025", streak:"3 months" },
        { id:2, name:"Marcus Torres", initials:"MT", color:"#2e5b7e", since:"Feb 2025", streak:"2 months" },
        { id:3, name:"Priya Kapoor",  initials:"PK", color:"#4a3a6b", since:"Feb 2025", streak:"2 months" },
        { id:4, name:"David Mensah",  initials:"DM", color:"#3a6b4a", since:"Mar 2025", streak:"1 month"  },
      ],
      risingDemos: [
        { id:1, title:"Midnight Run", color:"#6b3a5a", genre:"R&B",  plays:"234", cosigns:1, date:"Mar 2025" },
        { id:2, title:"First Light",  color:"#4a2a3a", genre:"Soul", plays:"141", cosigns:0, date:"Feb 2025" },
        { id:3, title:"Quiet Storm",  color:"#2a3a5a", genre:"R&B",  plays:"97",  cosigns:0, date:"Jan 2025" },
      ],
      risingCosigns: [
        { id:1, name:"Luna Wave", initials:"LW", color:"#2e5b7e", quote:"She's got a tone I haven't heard in years. Watch this one.", when:"2 weeks ago" },
      ],
      artistsWatching: [
        { id:1, name:"Luna Wave",   initials:"LW", color:"#2e5b7e", genre:"R&B"      },
        { id:2, name:"Cole Rivers", initials:"CR", color:"#7e5a2e", genre:"Soul"     },
        { id:3, name:"Sade Femi",   initials:"SF", color:"#3a6b4a", genre:"Amapiano" },
      ],
      discoveryTasks: [
        { label:"Add a bio",              pts:5,  done:true  },
        { label:"Drop a demo",            pts:15, done:true  },
        { label:"Add your genres",        pts:5,  done:true  },
        { label:"Get a co-sign",          pts:20, done:true  },
        { label:"Complete your location", pts:5,  done:false },
        { label:"Join the collab board",  pts:8,  done:false },
        { label:"Link social handles",    pts:5,  done:false },
        { label:"Get 5 demo plays",       pts:10, done:true  },
      ],
      navTabs: [
        { id:"home",     label:"Home",     badge:null, svg:`<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><polyline points="9 22 9 12 15 12 15 22" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>` },
        { id:"search",   label:"Discover", badge:null, svg:`<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="1.5"/><path d="M21 21l-4.35-4.35" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>` },
        { id:"drop",     label:"Drop",     badge:null, svg:`<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5"/><path d="M12 8v8M8 12l4-4 4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>` },
        { id:"rooms",    label:"Rooms",    badge:null, svg:`<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>` },
        { id:"messages", label:"Messages", badge:"3",  svg:`<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>` },
        { id:"collab",   label:"Collab",   badge:null, svg:`<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><circle cx="9" cy="7" r="4" stroke="currentColor" stroke-width="1.5"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>` },
        { id:"profile",  label:"Profile",  badge:null, svg:`<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><circle cx="12" cy="7" r="4" stroke="currentColor" stroke-width="1.5"/></svg>` },
      ],
    };
  },
  computed: {
    profile() {
      return this.profiles[this.currentRole];
    },
    userInitials() {
      return (this.profile?.name || "").split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
    },
  },
  mounted() {
    if (this.$route && this.$route.query.role) {
      this.currentRole = this.$route.query.role;
    }
  },
  methods: {
    navigate(tabId) {
      if (tabId === 'profile') return;
      this.$router.push(tabId === 'home' ? '/' : '/' + tabId);
    },
  },
};
</script>

<style scoped>
*{box-sizing:border-box;margin:0;padding:0}
.mz-desktop{display:grid;grid-template-columns:220px 1fr;min-height:100vh;background:#0c0a0e;font-family:'DM Sans','Helvetica Neue',sans-serif;color:#f5f0e8}
.mz-sidebar{background:#100e13;border-right:0.5px solid rgba(245,240,232,0.06);display:flex;flex-direction:column;position:sticky;top:0;height:100vh;overflow-y:auto}
.sidebar-top{flex:1;padding:24px 0 16px}
.sidebar-brand{display:flex;align-items:center;gap:10px;padding:0 20px 24px}
.sidebar-wordmark{font-size:19px;font-weight:500;color:#f5f0e8;letter-spacing:-0.3px}
.sidebar-nav{display:flex;flex-direction:column;gap:2px;padding:0 10px}
.nav-item{display:flex;align-items:center;gap:11px;padding:9px 12px;border-radius:9px;background:none;border:none;cursor:pointer;color:rgba(245,240,232,0.4);font-size:14px;font-family:inherit;text-align:left;transition:all .18s}
.nav-item:hover{background:rgba(245,240,232,0.04);color:rgba(245,240,232,0.75)}
.nav-item.active{background:rgba(212,140,28,0.1);color:#d48c1c}
.nav-item-icon{display:flex;align-items:center;flex-shrink:0}
.nav-item-label{flex:1}
.nav-badge{background:#d48c1c;color:#0c0a0e;font-size:10px;font-weight:700;border-radius:10px;padding:1px 6px;min-width:18px;text-align:center}
.sidebar-user{display:flex;align-items:center;gap:10px;padding:14px 18px;border-top:0.5px solid rgba(245,240,232,0.06)}
.su-av{width:32px;height:32px;border-radius:50%;flex-shrink:0;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:500;color:rgba(255,255,255,0.9)}
.su-info{flex:1;min-width:0}
.su-name{font-size:12px;font-weight:500;color:#f5f0e8;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.su-role{font-size:11px;color:rgba(245,240,232,0.4);margin-top:1px}
.su-logout{background:none;border:none;cursor:pointer;color:rgba(245,240,232,0.3);padding:3px;transition:color .2s;flex-shrink:0}
.su-logout:hover{color:rgba(245,240,232,0.65)}

/* Topbar */
.mz-content{display:flex;flex-direction:column;min-height:100vh;overflow:hidden}
.mz-topbar{display:flex;align-items:center;justify-content:space-between;padding:14px 28px;background:#0c0a0e;border-bottom:0.5px solid rgba(245,240,232,0.06);position:sticky;top:0;z-index:50}
.topbar-left{display:flex;align-items:center;gap:10px}
.topbar-title{font-size:17px;font-weight:500;color:#f5f0e8;letter-spacing:-0.2px}
.topbar-right{display:flex;align-items:center;gap:10px}
.role-switcher{display:flex;gap:5px}
.role-pill{background:none;border:0.5px solid rgba(245,240,232,0.1);border-radius:20px;padding:4px 13px;font-size:12px;color:rgba(245,240,232,0.4);font-family:inherit;cursor:pointer;transition:all .18s}
.role-pill.active{background:rgba(212,140,28,0.12);border-color:rgba(212,140,28,0.4);color:#d48c1c}
.topbar-icon{background:none;border:0.5px solid rgba(245,240,232,0.1);border-radius:8px;width:34px;height:34px;display:flex;align-items:center;justify-content:center;cursor:pointer;color:rgba(245,240,232,0.5);position:relative;transition:all .18s}
.topbar-icon:hover{border-color:rgba(245,240,232,0.25);color:#f5f0e8}
.t-badge{position:absolute;top:-5px;right:-5px;width:14px;height:14px;border-radius:50%;background:#d48c1c;color:#0c0a0e;font-size:9px;font-weight:700;display:flex;align-items:center;justify-content:center}

/* Color utils */
.amber{color:#d48c1c}
.teal{color:#5dcaa5}
@keyframes fadeUp{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}

/* Profile page wrapper */
.profile-page{flex:1;overflow-y:auto;animation:fadeUp .3s ease both}

/* ── HERO ── */
.profile-hero{padding:32px 28px 24px;border-bottom:0.5px solid rgba(245,240,232,0.07);position:relative;background:rgba(245,240,232,0.02)}
.hero-backdrop{position:absolute;inset:0;background:linear-gradient(135deg, rgba(var(--role-color-rgb, 44,91,126),0.06) 0%, transparent 60%);pointer-events:none}
.hero-content{display:flex;align-items:flex-start;justify-content:space-between;gap:20px;margin-bottom:16px}
.hero-left{display:flex;align-items:center;gap:16px}
.profile-avatar{width:72px;height:72px;border-radius:50%;flex-shrink:0;display:flex;align-items:center;justify-content:center;font-size:22px;font-weight:500;color:rgba(255,255,255,0.9);position:relative}
.av-verified{position:absolute;bottom:0;right:0;width:20px;height:20px;border-radius:50%;background:#d48c1c;border:2px solid #0c0a0e;display:flex;align-items:center;justify-content:center}
.hero-info{}
.hero-name{font-size:22px;font-weight:500;color:#f5f0e8;letter-spacing:-0.3px}
.hero-handle{font-size:13px;color:rgba(245,240,232,0.4);margin-top:3px}
.hero-tags{display:flex;gap:6px;flex-wrap:wrap;margin-top:8px}
.hero-tag{font-size:11px;background:rgba(245,240,232,0.06);border:0.5px solid rgba(245,240,232,0.1);border-radius:20px;padding:3px 10px;color:rgba(245,240,232,0.5)}
.hero-right{display:flex;flex-direction:column;align-items:flex-end;gap:14px}
.hero-stats{display:flex;align-items:center;gap:20px}
.hstat{text-align:center}
.hstat-num{font-size:20px;font-weight:500;color:#f5f0e8;letter-spacing:-0.3px}
.hstat-num.teal{color:#5dcaa5}
.hstat-label{font-size:11px;color:rgba(245,240,232,0.4);margin-top:3px}
.hstat-div{width:0.5px;height:32px;background:rgba(245,240,232,0.08)}
.hero-actions{display:flex;gap:8px}
.btn-edit{background:rgba(245,240,232,0.06);border:0.5px solid rgba(245,240,232,0.14);border-radius:9px;padding:8px 18px;font-size:13px;color:rgba(245,240,232,0.65);font-family:inherit;cursor:pointer;transition:all .18s}
.btn-edit:hover{border-color:rgba(245,240,232,0.28);color:#f5f0e8}
.btn-share{background:rgba(212,140,28,0.1);border:0.5px solid rgba(212,140,28,0.3);border-radius:9px;padding:8px 18px;font-size:13px;color:#d48c1c;font-family:inherit;cursor:pointer;transition:background .18s}
.btn-share:hover{background:rgba(212,140,28,0.2)}
.hero-bio{font-size:13px;color:rgba(245,240,232,0.55);line-height:1.6;max-width:600px}

/* ── BODY LAYOUT ── */
.profile-body{display:grid;grid-template-columns:1fr 300px;overflow:hidden}
.col-main{padding:24px 28px;overflow-y:auto;border-right:0.5px solid rgba(245,240,232,0.06)}
.col-side{padding:24px 22px;overflow-y:auto}

/* Shared section */
.section-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:14px}
.section-label{font-size:13px;font-weight:500;color:rgba(245,240,232,0.6);letter-spacing:0.2px}
.section-count{font-size:12px;color:rgba(245,240,232,0.35)}
.section-more{background:none;border:none;font-size:12px;color:#d48c1c;font-family:inherit;cursor:pointer;padding:0}

/* Edit card */
.edit-card{background:rgba(245,240,232,0.03);border:0.5px solid rgba(245,240,232,0.08);border-radius:13px;padding:20px;margin-bottom:28px}
.edit-title{font-size:14px;font-weight:500;color:#f5f0e8;margin-bottom:18px}
.edit-fields{display:flex;flex-direction:column;gap:14px}
.field-group{display:flex;flex-direction:column;gap:6px}
.field-group label{font-size:12px;color:rgba(245,240,232,0.45)}
.field-input{background:rgba(245,240,232,0.05);border:0.5px solid rgba(245,240,232,0.1);border-radius:8px;padding:9px 13px;font-size:13px;color:#f5f0e8;font-family:inherit;outline:none;transition:border-color .18s}
.field-input:focus{border-color:rgba(212,140,28,0.4)}
.field-ta{resize:vertical;min-height:80px;line-height:1.5}
.genre-check-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px}
.genre-check{display:flex;align-items:center;gap:7px;font-size:12px;color:rgba(245,240,232,0.55);cursor:pointer}
.genre-check input{accent-color:#d48c1c}

/* Fan subscriptions */
.sub-grid{display:flex;flex-direction:column;gap:8px}
.sub-card{display:flex;align-items:center;gap:12px;background:rgba(245,240,232,0.03);border:0.5px solid rgba(245,240,232,0.07);border-radius:11px;padding:13px 15px;transition:border-color .2s}
.sub-card:hover{border-color:rgba(245,240,232,0.14)}
.sub-av{width:42px;height:42px;border-radius:50%;flex-shrink:0;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:500;color:rgba(255,255,255,0.9);position:relative}
.av-live{position:absolute;bottom:-2px;right:-2px;background:#d48c1c;color:#0c0a0e;font-size:7px;font-weight:700;border-radius:4px;padding:1px 3px}
.sub-info{flex:1}
.sub-name{font-size:14px;font-weight:500;color:#f5f0e8}
.sub-genre{font-size:12px;color:rgba(245,240,232,0.4);margin-top:2px}
.sub-since{font-size:11px;color:rgba(245,240,232,0.28);margin-top:2px}
.sub-right{display:flex;flex-direction:column;align-items:flex-end;gap:8px}
.sub-tier{font-size:10px;font-weight:500;border-radius:20px;padding:3px 10px}
.tier-member{background:rgba(212,140,28,0.12);border:0.5px solid rgba(212,140,28,0.3);color:#d48c1c}
.sub-manage{background:none;border:0.5px solid rgba(245,240,232,0.1);border-radius:20px;padding:4px 12px;font-size:11px;color:rgba(245,240,232,0.4);font-family:inherit;cursor:pointer;transition:all .18s}
.sub-manage:hover{border-color:rgba(245,240,232,0.25);color:rgba(245,240,232,0.7)}

/* Listening history */
.history-list{display:flex;flex-direction:column;gap:2px}
.history-row{display:flex;align-items:center;gap:10px;padding:9px 10px;border-radius:8px;transition:background .18s}
.history-row:hover{background:rgba(245,240,232,0.04)}
.h-thumb{width:34px;height:34px;border-radius:7px;flex-shrink:0;display:flex;align-items:center;justify-content:center}
.h-info{flex:1}
.h-track{font-size:13px;color:#f5f0e8}
.h-artist{font-size:11px;color:rgba(245,240,232,0.4);margin-top:1px}
.h-time{font-size:11px;color:rgba(245,240,232,0.28);white-space:nowrap}
.h-play{width:26px;height:26px;border-radius:50%;background:rgba(212,140,28,0.12);border:0.5px solid rgba(212,140,28,0.3);display:flex;align-items:center;justify-content:center;font-size:8px;color:#d48c1c;cursor:pointer;flex-shrink:0}

/* Account card */
.account-card{background:rgba(245,240,232,0.03);border:0.5px solid rgba(245,240,232,0.07);border-radius:11px;overflow:hidden;margin-bottom:10px}
.ac-row{display:flex;align-items:center;justify-content:space-between;padding:10px 14px;border-bottom:0.5px solid rgba(245,240,232,0.05)}
.ac-row:last-child{border-bottom:none}
.ac-label{font-size:12px;color:rgba(245,240,232,0.4)}
.ac-val{font-size:12px;color:rgba(245,240,232,0.7)}
.ac-val.amber{color:#d48c1c}
.ac-val.teal{color:#5dcaa5}
.upgrade-btn{width:100%;background:#d48c1c;border:none;border-radius:9px;padding:10px;font-size:13px;font-weight:500;color:#0c0a0e;font-family:inherit;cursor:pointer;transition:opacity .18s}
.upgrade-btn:hover{opacity:.88}
.tag-cloud{display:flex;gap:6px;flex-wrap:wrap}
.tag-pill{font-size:11px;background:rgba(245,240,232,0.06);border:0.5px solid rgba(245,240,232,0.1);border-radius:20px;padding:4px 11px;color:rgba(245,240,232,0.5)}
.perk-list{display:flex;flex-direction:column;gap:8px}
.perk-row{display:flex;align-items:center;gap:9px;font-size:13px;color:rgba(245,240,232,0.35)}
.perk-row.locked{color:rgba(245,240,232,0.25)}

/* Earnings strip */
.earnings-strip{display:grid;grid-template-columns:repeat(4,1fr);gap:10px}
.earn-tile{background:rgba(212,140,28,0.07);border:0.5px solid rgba(212,140,28,0.18);border-radius:11px;padding:16px 14px}
.et-num{font-size:22px;font-weight:500;color:#d48c1c;letter-spacing:-0.5px}
.et-label{font-size:11px;color:rgba(245,240,232,0.4);margin-top:4px}

/* Release table */
.release-table{background:rgba(245,240,232,0.03);border:0.5px solid rgba(245,240,232,0.07);border-radius:11px;overflow:hidden}
.rt-head{display:grid;grid-template-columns:1fr 80px 90px 90px 80px;padding:9px 14px;border-bottom:0.5px solid rgba(245,240,232,0.07);font-size:11px;color:rgba(245,240,232,0.3);letter-spacing:0.4px}
.rt-row{display:grid;grid-template-columns:1fr 80px 90px 90px 80px;padding:11px 14px;border-bottom:0.5px solid rgba(245,240,232,0.05);align-items:center;transition:background .18s}
.rt-row:last-child{border-bottom:none}
.rt-row:hover{background:rgba(245,240,232,0.03)}
.rt-track{display:flex;align-items:center;gap:9px}
.rt-art{width:32px;height:32px;border-radius:7px;flex-shrink:0;display:flex;align-items:center;justify-content:center}
.rt-title{font-size:13px;color:#f5f0e8}
.rt-date{font-size:11px;color:rgba(245,240,232,0.3);margin-top:1px}
.rt-stat{font-size:13px;color:rgba(245,240,232,0.45)}
.rt-earn{font-size:13px;font-weight:500;color:#5dcaa5}
.rt-status{font-size:10px;font-weight:500;border-radius:20px;padding:3px 9px;display:inline-block}
.status-members{background:rgba(212,140,28,0.12);border:0.5px solid rgba(212,140,28,0.3);color:#d48c1c}
.status-public{background:rgba(93,202,165,0.1);border:0.5px solid rgba(93,202,165,0.25);color:#5dcaa5}

/* Upcoming rooms */
.upcoming-rooms{display:flex;flex-direction:column;gap:8px}
.upcoming-room-row{display:flex;align-items:center;gap:12px;background:rgba(245,240,232,0.03);border:0.5px solid rgba(245,240,232,0.07);border-radius:11px;padding:13px 15px;transition:border-color .2s}
.upcoming-room-row:hover{border-color:rgba(245,240,232,0.14)}
.urr-time{min-width:70px}
.urr-val{font-size:14px;font-weight:500;color:#f5f0e8}
.urr-day{font-size:11px;color:rgba(245,240,232,0.35);margin-top:2px}
.urr-div{width:0.5px;height:36px;background:rgba(245,240,232,0.08);flex-shrink:0}
.urr-info{flex:1;min-width:0}
.urr-title{font-size:13px;color:#f5f0e8;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.urr-type-pill{font-size:10px;font-weight:500;border-radius:20px;padding:3px 9px;display:inline-flex;margin-top:4px}
.pill-session{background:rgba(212,140,28,0.12);border:0.5px solid rgba(212,140,28,0.3);color:#d48c1c}
.pill-qa{background:rgba(127,119,221,0.12);border:0.5px solid rgba(127,119,221,0.3);color:#7F77DD}
.pill-collab{background:rgba(93,202,165,0.12);border:0.5px solid rgba(93,202,165,0.3);color:#5dcaa5}
.urr-rsvp{font-size:12px;color:rgba(245,240,232,0.35);white-space:nowrap}
.urr-edit{background:none;border:0.5px solid rgba(245,240,232,0.1);border-radius:20px;padding:4px 12px;font-size:11px;color:rgba(245,240,232,0.4);font-family:inherit;cursor:pointer;transition:all .18s;flex-shrink:0}
.urr-edit:hover{border-color:rgba(245,240,232,0.25);color:rgba(245,240,232,0.7)}

/* Public profile preview (side) */
.public-profile-preview{background:rgba(245,240,232,0.03);border:0.5px solid rgba(245,240,232,0.08);border-radius:13px;padding:18px;display:flex;flex-direction:column;gap:8px;align-items:center;text-align:center}
.pp-av{width:52px;height:52px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:16px;font-weight:500;color:rgba(255,255,255,0.9)}
.pp-name{font-size:15px;font-weight:500;color:#f5f0e8}
.pp-handle{font-size:12px;color:rgba(245,240,232,0.4)}
.pp-bio{font-size:12px;color:rgba(245,240,232,0.5);line-height:1.5;text-align:left}
.pp-tags{display:flex;gap:5px;flex-wrap:wrap;justify-content:center}
.pp-collab-note{font-size:11px;color:#5dcaa5;background:rgba(93,202,165,0.07);border:0.5px solid rgba(93,202,165,0.2);border-radius:8px;padding:6px 10px;width:100%;text-align:left}
.pp-subscribe{width:100%;background:#d48c1c;border:none;border-radius:9px;padding:9px;font-size:13px;font-weight:500;color:#0c0a0e;font-family:inherit;cursor:pointer;transition:opacity .18s}
.pp-subscribe:hover{opacity:.85}
.pp-view{width:100%;background:none;border:0.5px solid rgba(245,240,232,0.1);border-radius:9px;padding:8px;font-size:12px;color:rgba(245,240,232,0.4);font-family:inherit;cursor:pointer;transition:all .18s}
.pp-view:hover{border-color:rgba(245,240,232,0.25);color:rgba(245,240,232,0.7)}

/* Top subs mini list */
.sub-list-mini{display:flex;flex-direction:column;gap:4px}
.slm-row{display:flex;align-items:center;gap:9px;padding:7px 9px;border-radius:9px;transition:background .18s}
.slm-row:hover{background:rgba(245,240,232,0.04)}
.slm-av{width:34px;height:34px;border-radius:50%;flex-shrink:0;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:500;color:rgba(255,255,255,0.9)}
.slm-info{flex:1;min-width:0}
.slm-name{font-size:13px;color:#f5f0e8;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.slm-since{font-size:11px;color:rgba(245,240,232,0.35);margin-top:1px}
.slm-streak{font-size:11px;color:rgba(245,240,232,0.3);white-space:nowrap}
.slm-reach{background:none;border:0.5px solid rgba(93,202,165,0.25);border-radius:20px;padding:4px 11px;font-size:11px;color:#5dcaa5;font-family:inherit;cursor:pointer;transition:all .18s;flex-shrink:0}
.slm-reach:hover{background:rgba(93,202,165,0.08)}

/* Discovery card (rising) */
.discovery-card-full{display:flex;gap:32px;background:rgba(93,202,165,0.06);border:0.5px solid rgba(93,202,165,0.2);border-radius:13px;padding:24px}
.dcf-left{min-width:160px}
.dcf-eyebrow{font-size:11px;color:rgba(245,240,232,0.4);letter-spacing:0.5px;margin-bottom:6px}
.dcf-num{font-size:52px;font-weight:500;line-height:1}
.dcf-max{font-size:18px;color:rgba(245,240,232,0.3)}
.dcf-bar-wrap{margin:12px 0 8px}
.dcf-bar{height:4px;background:rgba(245,240,232,0.08);border-radius:2px;overflow:hidden}
.dcf-fill{height:100%;background:#5dcaa5;border-radius:2px;transition:width .4s ease}
.dcf-sub{font-size:12px;color:rgba(245,240,232,0.45);line-height:1.5}
.dcf-right{flex:1}
.dcf-label{font-size:12px;font-weight:500;color:rgba(245,240,232,0.45);letter-spacing:0.3px;margin-bottom:12px}
.dcf-tasks{display:flex;flex-direction:column;gap:8px}
.dcf-task{display:flex;align-items:center;gap:10px;font-size:13px;color:rgba(245,240,232,0.55);transition:opacity .18s}
.dcf-task.done{opacity:.45}
.task-check{width:18px;height:18px;border-radius:50%;border:0.5px solid rgba(245,240,232,0.2);flex-shrink:0;display:flex;align-items:center;justify-content:center;color:rgba(245,240,232,0.3)}
.task-check.done{background:#5dcaa5;border-color:#5dcaa5;color:#0c0a0e}
.task-label{flex:1}
.task-pts{font-size:11px;font-weight:500;color:#5dcaa5;white-space:nowrap}
.task-pts.done{color:rgba(245,240,232,0.25)}

/* Demo table (rising) */
.demo-table{background:rgba(245,240,232,0.03);border:0.5px solid rgba(245,240,232,0.07);border-radius:11px;overflow:hidden}
.dt-head{display:grid;grid-template-columns:1fr 80px 80px 90px;padding:9px 14px;border-bottom:0.5px solid rgba(245,240,232,0.07);font-size:11px;color:rgba(245,240,232,0.3);letter-spacing:0.4px}
.dt-row{display:grid;grid-template-columns:1fr 80px 80px 90px;padding:11px 14px;border-bottom:0.5px solid rgba(245,240,232,0.05);align-items:center;transition:background .18s}
.dt-row:last-child{border-bottom:none}
.dt-row:hover{background:rgba(245,240,232,0.03)}
.dt-track{display:flex;align-items:center;gap:9px}
.dt-art{width:32px;height:32px;border-radius:7px;flex-shrink:0;display:flex;align-items:center;justify-content:center}
.dt-title{font-size:13px;color:#f5f0e8}
.dt-genre{font-size:11px;color:rgba(245,240,232,0.35);margin-top:1px}
.dt-stat{font-size:13px;color:rgba(245,240,232,0.45)}
.dt-cosigns{font-size:13px;color:rgba(245,240,232,0.45)}
.dt-date{font-size:11px;color:rgba(245,240,232,0.3)}

/* Co-signs received */
.cosign-received-list{display:flex;flex-direction:column;gap:10px}
.csr-row{display:flex;align-items:flex-start;gap:11px;background:rgba(93,202,165,0.04);border:0.5px solid rgba(93,202,165,0.15);border-radius:11px;padding:14px}
.csr-av{width:38px;height:38px;border-radius:50%;flex-shrink:0;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:500;color:rgba(255,255,255,0.9)}
.csr-body{flex:1}
.csr-from{font-size:13px;font-weight:500;color:#f5f0e8}
.csr-role{font-size:11px;color:rgba(245,240,232,0.35);font-weight:400}
.csr-quote{font-size:13px;color:rgba(245,240,232,0.6);margin-top:6px;line-height:1.5;font-style:italic}
.csr-when{font-size:11px;color:rgba(245,240,232,0.28);white-space:nowrap;flex-shrink:0}

/* Collab note box */
.collab-note-box{font-size:12px;color:rgba(245,240,232,0.5);background:rgba(93,202,165,0.06);border:0.5px solid rgba(93,202,165,0.18);border-radius:9px;padding:10px 13px;line-height:1.5}

/* Empty state */
.empty-state{font-size:13px;color:rgba(245,240,232,0.3);padding:20px 0}
</style>